import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroSectionProps {
  title: string;
  subtitle: string;
  description: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ title, subtitle, description }) => {
  const { t } = useLanguage();
  
  return (
    <section className="relative bg-gradient-to-br from-primary via-secondary to-brand-primary py-20 lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(0,168,204,0.1),transparent_50%)]" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Icon name="BuildingOfficeIcon" size={20} className="text-accent" variant="solid" />
            <span className="text-sm font-cta font-semibold text-white">{subtitle}</span>
          </div>
          
          <h1 className="font-headline font-bold text-4xl lg:text-5xl xl:text-6xl text-white mb-6 leading-tight">
            {t('about.hero.title')}
          </h1>
          
          <p className="font-body text-lg lg:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
            {t('about.hero.description')}
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="UserGroupIcon" size={24} className="text-accent" variant="solid" />
              <div className="text-left">
                <p className="text-2xl font-headline font-bold text-white">50+</p>
                <p className="text-xs font-body text-white/80">{t('about.hero.teamMembers')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="ClockIcon" size={24} className="text-accent" variant="solid" />
              <div className="text-left">
                <p className="text-2xl font-headline font-bold text-white">15+</p>
                <p className="text-xs font-body text-white/80">{t('about.hero.yearsExperience')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-lg">
              <Icon name="StarIcon" size={24} className="text-accent" variant="solid" />
              <div className="text-left">
                <p className="text-2xl font-headline font-bold text-white">500+</p>
                <p className="text-xs font-body text-white/80">{t('about.hero.successfulProjects')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;